import { yupResolver } from "@hookform/resolvers/yup";
import { DialogActions, Slide, type SlideProps, useTheme } from "@mui/material";
import { type FC, type ReactNode, forwardRef, useEffect, useState } from "react";
import { type Control, type FieldValues, type UseFormGetValues, type UseFormSetValue, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as yup from "yup";
import "yup-phone-lite";

import { emailRegex, validSMS } from "@/config/regex";
import { MEDIA_SIZE, MESSAGE_TYPE } from "@/constants";
import { PHONE_DEFAULT_VALUE } from "@/constants/phoneDefaultValue";
import { useUrlParams } from "@/hooks";
import { MessageService } from "@/services";
import { useThunkDispatch } from "@/store/hooks";
import { currentPropertySelector, simpleValuesSelector } from "@/store/selectors";
import { setSimpleValue } from "@/store/simpleValues/actions";
import type { Phone, User } from "@/store/v2/currentUser/selectors";
import { getMediaLink } from "@/utils/mediaLink";
import { nameValidationSchema } from "@/utils/validationSchemas";

import * as S from "./styled";
import DigitalBusinessCardContent from "../DigitalBusinessCardContent";

const determineMessageType = ({
  email,
  phone,
}: {
  email?: string;
  phone: {
    number?: string;
  };
}) => {
  if (email && phone.number) {
    return MESSAGE_TYPE.BOTH;
  } else if (email) {
    return MESSAGE_TYPE.EMAIL;
  } else if (phone?.number) {
    return MESSAGE_TYPE.TEXT;
  }
};

const Transition = forwardRef(({ children, ...props }: { children: ReactNode } & SlideProps, ref) => (
  <Slide direction="down" ref={ref} {...props}>
    {children}
  </Slide>
));

const contactDialogFormSchema = yup.object({
  name: nameValidationSchema,
  phone: yup.object().shape({
    code: yup.string().required("Required"),
    iso: yup.string().required("Required"),
    number: yup
      .string()
      .ensure()
      .when(["code", "iso"], {
        is: (code: string, iso: string) => code && iso,
        then: (schema) => {
          return schema.test("is-valid-phone-number", "Please provide a valid phone number", function (value) {
            return yup.string().phone(this.parent.iso).isValidSync(value);
          });
        },
      })
      .test("required", "Either phone or email is required", (value, values) => {
        const email = values?.from?.[1]?.value?.email;
        return !!(value || email);
      }),
  }),
  email: yup
    .string()
    .test("required", "Either phone or email is required", (value, values) => {
      const { phone } = values.parent;

      return !!(value || phone.number);
    })
    .test("invalid", "Invalid Email", async (value) => {
      if (value === "") return true;
      return emailRegex.test(`${value}`);
    }),
  message: yup.string().matches(validSMS, "Message contains invalid characters.").required("Message is required."),
});

interface ContactDialogV2Props {
  readonly dbcContact?: Partial<User>;
}

const ContactDialogV2: FC<ContactDialogV2Props> = (props) => {
  const { dbcContact = {} } = props;

  const dispatch = useThunkDispatch();

  const simpleValues = useSelector(simpleValuesSelector);

  const shareKey = "contact_salesperson";
  const isDialogOpen = !!simpleValues?.[shareKey];

  const { propertyId, experienceId } = useUrlParams();
  const { property } = useSelector(currentPropertySelector);

  const theme = useTheme();

  const photo = getMediaLink({ size: MEDIA_SIZE.small, link: dbcContact?.photoUrl });
  const [createdByPhone = {} as Partial<Phone>] = dbcContact?.phones || [];
  const [messageSent, setMessageSent] = useState(false);

  const {
    handleSubmit,
    formState: { isDirty, isValid, isSubmitting },
    getValues,
    setValue,
    control,
    reset,
    trigger,
  } = useForm({
    mode: "all",
    resolver: yupResolver(contactDialogFormSchema),
    defaultValues: {
      name: "",
      phone: PHONE_DEFAULT_VALUE,
      email: "",
      message: "",
    },
  });
  const values = getValues();

  const disabled = !isDirty || !isValid || isSubmitting;

  useEffect(() => {
    void trigger();
  }, [values.email, values.phone]);

  const onCloseDialog = () => {
    reset();
    dispatch(setSimpleValue(shareKey, undefined));
    setMessageSent(false);
  };

  const onSubmit = async (valuesToSubmit: typeof values) => {
    const type = determineMessageType(valuesToSubmit);
    const { name: senderName, message: senderText, email: senderEmail, phone: senderPhone } = valuesToSubmit;

    try {
      const data = {
        type,
        senderName,
        senderText,
        ...(senderEmail && { senderEmail }),
        ...(senderPhone?.number && { senderPhone: senderPhone.number }),
        shareUrl: window.location.href,
      };
      const { err } = await MessageService.addMessage(propertyId, experienceId, data);
      setMessageSent(true);

      if (!err) {
        toast.success(`Sent ${type} to contact`);
      }
    } catch {
      toast.error("Error sending to contact", {
        containerId: "contactDialog",
      });
    }
  };

  const validate = async () => {
    try {
      await trigger();
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <S.ModalDialog
      open={isDialogOpen}
      onClose={onCloseDialog}
      aria-labelledby="form-dialog-title"
      TransitionComponent={Transition}
      maxWidth="md"
      data-testid="contact-dialog"
    >
      <S.ModalTitle id="dialog-title" data-testid="dialog-title">
        Contact
      </S.ModalTitle>
      {!messageSent ? (
        <S.ModalContent data-testid="dialog-content">
          <DigitalBusinessCardContent
            photo={photo}
            name={dbcContact?.name || ""}
            email={(dbcContact?.displayEmail && dbcContact?.email) || ""}
            phone={(dbcContact?.displayWorkPhone && createdByPhone?.number) || ""}
            control={control as unknown as Control<FieldValues>}
            getValues={getValues as unknown as UseFormGetValues<FieldValues>}
            setValue={setValue as unknown as UseFormSetValue<FieldValues>}
            validate={validate}
            data-testid="digital-business-card"
          />
        </S.ModalContent>
      ) : (
        <S.ModalContent data-testid="success-message">
          <S.MessageContent>
            <S.AvatarInfo variant="h6" align="center">
              Your message was sent!
            </S.AvatarInfo>
            <S.AvatarInfo variant="h6" align="center">
              Thank you for your interest in
            </S.AvatarInfo>
            <S.AvatarInfo
              variant="h6"
              align="center"
              sx={{
                fontSize: "24px !important",
                color: "#F50561 !important",
                lineHeight: "32px !important",
                fontWeight: "400 !important",
              }}
            >
              {property?.name}
            </S.AvatarInfo>
            <S.AvatarInfo variant="h6" align="center">
              {dbcContact?.name} will be in touch soon.
            </S.AvatarInfo>
          </S.MessageContent>
        </S.ModalContent>
      )}

      {!messageSent ? (
        <DialogActions>
          <S.Button $theme={theme} onClick={onCloseDialog} data-testid="cancel-button">
            Cancel
          </S.Button>
          <S.Button
            $theme={theme}
            className="submitButton"
            onClick={handleSubmit(onSubmit)}
            disabled={disabled}
            data-testid="submit-button"
          >
            Submit
          </S.Button>
        </DialogActions>
      ) : (
        <DialogActions>
          <S.Button $theme={theme} onClick={onCloseDialog} data-testid="close-button">
            Close
          </S.Button>
        </DialogActions>
      )}
    </S.ModalDialog>
  );
};

export default ContactDialogV2;
